'use client';

import * as React from 'react';
import Image from 'next/image';
import { useEventListener } from '@react-hookz/web';
import clsx from 'clsx';
import { ProgressCircle } from 'components/charts/progress-circle';
import { IconWrapper } from 'components/icon-wrapper';
import { IconApple } from 'icon/Brands/apple';
import { IconBarChart04 } from 'icon/Charts/bar-chart-04';
import { IconMessageChatSquare } from 'icon/Communication/message-chat-square';
import { IconFeather } from 'icon/Editor/feather';
import { IconPalette } from 'icon/Editor/palette';
import { IconCalculator } from 'icon/Education/calculator';
import { IconFile06 } from 'icon/Files/file-06';
import { IconSearchMd } from 'icon/General/search-md';
import { IconZapFast } from 'icon/General/zap-fast';
import { IconGlobe01 } from 'icon/Maps&travel/globe-01';
import { BlurFade, FocusableLink, Separator } from 'ui';
import analyticsImg from '../../../../public/images/root/features/analytics.png';
import notesAppImg from '../../../../public/images/root/features/apple-notes.png';
import designImg from '../../../../public/images/root/features/design.png';
import styles from './features.module.css';
import { NewsletterFeature } from './newsletter-feature';

let current: HTMLElement | null = null;

export function Features(): JSX.Element {
  const ulRef = React.useRef<HTMLUListElement>(null);
  useEventListener(ulRef, 'pointermove', (event: PointerEvent) => {
    const { x, y } = event;
    const article = document
      .elementFromPoint(x, y)
      ?.closest('li')
      ?.querySelector('article');
    if (article !== current) {
      current = article || null;
      // Set the bounds
      if (current) {
        const bounds = current.getBoundingClientRect();
        ulRef.current?.style.setProperty(
          '--top',
          String(bounds.top + window.scrollY),
        );
        ulRef.current?.style.setProperty(
          '--right',
          String(bounds.right + window.scrollX),
        );
        ulRef.current?.style.setProperty(
          '--bottom',
          String(bounds.bottom + window.scrollY),
        );
        ulRef.current?.style.setProperty(
          '--left',
          String(bounds.left + window.scrollX),
        );
        ulRef.current?.style.setProperty('--height', String(bounds.height));
        ulRef.current?.style.setProperty('--width', String(bounds.width));
      }
    }
  });
  return (
    <div className="bg-gray-50 px-4">
      <div
        className="mx-auto flex max-w-7xl flex-col items-center space-y-4 px-4 py-16 text-center xs:py-24"
        id="features"
      >
        <BlurFade delay={0.25}>
          <p className="text-md font-semibold text-brand-700">Features</p>
        </BlurFade>
        <BlurFade delay={0.2}>
          <h2 className="text-balance text-display-md font-semibold text-gray-900">
            You write good content, we take care of the rest
          </h2>
        </BlurFade>
        <section className="mx-auto flex w-full max-w-7xl flex-col items-start space-y-24 py-16 xs:py-24">
          <div className="flex flex-col items-start text-start">
            <BlurFade delay={0.2}>
              <IconWrapper className="mb-6">
                <IconFeather />
              </IconWrapper>
            </BlurFade>
            <BlurFade delay={0.1}>
              <h2 className="mb-4 text-display-sm font-semibold">
                100% ownership of your data
              </h2>
            </BlurFade>
            <BlurFade delay={0.2}>
              <p className="mb-6 max-w-5xl text-lg text-gray-600">
                Create, publish, and build your brand hassle-free with Quotion.
                Keep your content in{' '}
                <span className="font-semibold">Apple Notes</span> while we
                handle the technical side of website publishing. Say goodbye to{' '}
                <span className="font-semibold">security</span>,{' '}
                <span className="font-semibold">privacy</span>,{' '}
                <span className="font-semibold">downtime</span>, and{' '}
                <span className="font-semibold">performance</span> concerns.
                Focus on what matters most: your brand and content creation.
              </p>
            </BlurFade>
            <BlurFade delay={0.25} yOffset={6}>
              <Image
                alt="Apple Notes screenshot"
                height={1792}
                src={notesAppImg}
                placeholder="blur"
                width={2404}
              />
            </BlurFade>
          </div>
          <NewsletterFeature />
          <div className="flex max-w-7xl flex-col items-start text-start">
            <BlurFade delay={0.2}>
              <IconWrapper className="mb-6">
                <IconPalette />
              </IconWrapper>
            </BlurFade>
            <BlurFade delay={0.1}>
              <h2 className="mb-4 text-display-sm font-semibold">
                Customize to reflect your brand
              </h2>
            </BlurFade>
            <BlurFade delay={0.2}>
              <p className="mb-6 max-w-5xl text-lg text-gray-600">
                Personalize the appearance and ambiance of your site with design
                editor effortlessly. You can customize colors, fonts, and
                controls inside Quotion without code to make you proud of the
                unique site you share with the world. Only add custom code (CSS
                and JS) if you want to.
              </p>
            </BlurFade>
            <BlurFade delay={0.25} yOffset={6}>
              <Image
                alt="Design editor"
                className="rounded-lg border"
                height={1323}
                src={designImg}
                placeholder="blur"
                width={1440}
              />
            </BlurFade>
          </div>
          <div className="flex max-w-7xl flex-col items-start text-start">
            <BlurFade delay={0.2}>
              <IconWrapper className="mb-6">
                <IconBarChart04 />
              </IconWrapper>
            </BlurFade>
            <BlurFade delay={0.1}>
              <h2 className="mb-4 text-display-sm font-semibold">
                Built-in and privacy-friendly analytics
              </h2>
            </BlurFade>
            <BlurFade delay={0.2}>
              <p className="mb-6 max-w-5xl text-lg text-gray-600">
                <span>
                  Get to know your audience. Track page views, visitor behavior,
                  and more with our comprehensive analytics. No setup needed.
                </span>
                <FocusableLink
                  className="cursor-pointer ps-2 underline"
                  href="https://docs.quotion.co/features/web-analytics"
                  target="_blank"
                >
                  Learn more
                </FocusableLink>
                .
              </p>
            </BlurFade>
            <BlurFade delay={0.25} yOffset={6}>
              <Image
                alt="Web analytics"
                className="rounded-lg border"
                height={962}
                src={analyticsImg}
                placeholder="blur"
                width={1440}
              />
            </BlurFade>
          </div>
          <div className="flex w-full flex-col items-start text-start">
            <BlurFade delay={0.2}>
              <IconWrapper className="mb-6">
                <IconZapFast />
              </IconWrapper>
            </BlurFade>
            <BlurFade delay={0.1}>
              <h2
                className="mb-4 text-display-sm font-semibold"
                id="performance"
              >
                Turbocharged performance, out of the box
              </h2>
            </BlurFade>
            <BlurFade delay={0.2}>
              <p className="mb-6 max-w-5xl text-lg text-gray-600">
                Quotion goes beyond blog site creation, empowering you to
                connect and engage with your audience effortlessly. Our
                automatic optimizations handle the technical details, allowing
                you to focus on crafting compelling content delivered at
                lightning-fast speeds.
              </p>
            </BlurFade>
            <BlurFade
              delay={0.25}
              yOffset={6}
              className="w-full rounded-lg border border-gray-300 bg-white p-8"
            >
              <div className="flex flex-wrap-reverse justify-between gap-8">
                <div className="flex flex-col items-center gap-2">
                  <ProgressCircle value={97}>97</ProgressCircle>
                  <p className="text-md font-medium text-gray-600 sm:text-lg">
                    Performance
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <ProgressCircle value={100}>100</ProgressCircle>
                  <p className="text-md font-medium text-gray-600 sm:text-lg">
                    SEO
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <ProgressCircle value={100}>100</ProgressCircle>
                  <p className="text-md font-medium text-gray-600 sm:text-lg">
                    Accessibility
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <ProgressCircle value={96}>96</ProgressCircle>
                  <p className="text-md font-medium text-gray-600 sm:text-lg">
                    Best practices
                  </p>
                </div>
              </div>
              <Separator className="my-8" />
              <div className="pb-4">
                <h3 className="pb-4 text-lg font-semibold text-gray-700">
                  Performance Metrics
                </h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex gap-2 rounded bg-gray-50 p-4">
                    <div className="size-4 rounded-full bg-green-500" />
                    <div className="space-y-1">
                      <p className="text-md leading-none text-gray-600">
                        First Contentful Paint
                      </p>
                      <p className="text-2xl font-semibold text-green-600">
                        0.5s
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 rounded bg-gray-50 p-4">
                    <div className="size-3.5 bg-yellow-500" />
                    <div className="space-y-1">
                      <p className="text-md leading-none text-gray-600">
                        Largest Contentful Paint
                      </p>
                      <p className="text-2xl font-semibold text-yellow-600">
                        1.3s
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 rounded bg-gray-50 p-4">
                    <div className="size-4 rounded-full bg-green-500" />
                    <div className="space-y-1">
                      <p className="text-md leading-none text-gray-600">
                        Total Blocking Time
                      </p>
                      <p className="text-2xl font-semibold text-green-600">
                        20ms
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 rounded bg-gray-50 p-4">
                    <div className="size-4 rounded-full bg-green-500" />
                    <div className="space-y-1">
                      <p className="text-md leading-none text-gray-600">
                        Cumulative Layout Shift
                      </p>
                      <p className="text-2xl font-semibold text-green-600">0</p>
                    </div>
                  </div>
                  <div className="flex gap-2 rounded bg-gray-50 p-4">
                    <div className="size-4 rounded-full bg-green-500" />
                    <div className="space-y-1">
                      <p className="text-md leading-none text-gray-600">
                        Speed Index
                      </p>
                      <p className="text-2xl font-semibold text-green-600">
                        0.8s
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                * Real Google pagespeed data of Quotion blog
              </p>
            </BlurFade>
          </div>
        </section>
        <section className={clsx(styles.container)}>
          <BlurFade delay={0.2}>
            <h3 className="mb-4 text-display-sm font-semibold">
              More features you would love
            </h3>
          </BlurFade>
          <ul
            className="grid grid-cols-1 gap-x-8 gap-y-16 pt-16 sm:grid-cols-2 md:grid-cols-3"
            data-enhanced
            ref={ulRef}
          >
            <FeatureCard
              description={
                <>
                  <span>
                    Experience full compatibility with Apple Notes. Enjoy
                    seamless integration with all of its features, including
                    rich text, tags, images, videos, PDFs, and much more.
                  </span>
                  <FocusableLink
                    className="cursor-pointer ps-2 underline"
                    href="https://docs.quotion.co/features/apple-notes"
                    target="_blank"
                  >
                    Learn more
                  </FocusableLink>
                  .
                </>
              }
              icon={<IconApple />}
              title="Apple Notes first"
              delay={0.1}
            />
            <FeatureCard
              description={
                <>
                  <span>
                    Easy indexing by search engines with well-crafted markup and
                    auto-generated sitemap.xml, rss.xml, robots.txt, etc.
                  </span>
                  <FocusableLink
                    className="cursor-pointer ps-2 underline"
                    href="https://docs.quotion.co/features/seo"
                    target="_blank"
                  >
                    Learn more
                  </FocusableLink>
                </>
              }
              icon={<IconSearchMd />}
              title="SEO Friendly"
              delay={0.15}
            />
            <FeatureCard
              description={
                <>
                  <span>
                    Link your custom domain for unique branding or start quickly
                    with a free quotion.co subdomain.
                  </span>
                  <FocusableLink
                    className="cursor-pointer ps-2 underline"
                    href="https://docs.quotion.co/features/custom-domain"
                    target="_blank"
                  >
                    Learn more
                  </FocusableLink>
                  .
                </>
              }
              icon={<IconGlobe01 />}
              title="Custom domain"
              delay={0.2}
            />
            <FeatureCard
              description={
                <>
                  <span>
                    Quotion has a built-in comment system that can help you in
                    effortlessly engaging with and retaining your users.
                  </span>
                  <FocusableLink
                    className="cursor-pointer ps-2 underline"
                    href="https://docs.quotion.co/features/comments"
                    target="_blank"
                  >
                    Learn more
                  </FocusableLink>
                  .
                </>
              }
              icon={<IconMessageChatSquare />}
              title="Comment system"
              delay={0.25}
            />
            <FeatureCard
              description={
                <>
                  <span>
                    Enhance your writing experience with markdown, and export
                    your notes as markdown files.
                  </span>
                  <FocusableLink
                    className="cursor-pointer ps-2 underline"
                    href="https://docs.quotion.co/features/markdown"
                    target="_blank"
                  >
                    Learn more.
                  </FocusableLink>
                </>
              }
              icon={<IconFile06 />}
              title="Enhance with markdown"
              delay={0.3}
            />
            <FeatureCard
              description={
                <>
                  <span>
                    Write math expressions with KaTeX in your notes, it works
                    perfectly well on your sites.
                  </span>
                  <FocusableLink
                    className="cursor-pointer ps-2 underline"
                    href="https://docs.quotion.co/features/katex"
                    target="_blank"
                  >
                    Learn more.
                  </FocusableLink>
                </>
              }
              icon={<IconCalculator />}
              title="KaTeX"
              delay={0.35}
            />
          </ul>
        </section>
      </div>
    </div>
  );
}

type FeatureCardProps = {
  title: string;
  description: React.ReactNode;
  icon: React.ReactNode;
  delay: number;
};

function FeatureCard({ title, description, icon, delay }: FeatureCardProps) {
  return (
    <li>
      <BlurFade delay={delay}>
        <article className="group flex select-none flex-col items-start text-start">
          <div className="w-fit rounded-md border bg-white p-3 text-gray-700 transition group-hover:border-gray-300">
            {icon}
          </div>
          <h3 className="pt-5 text-lg font-semibold text-gray-900">{title}</h3>
          <p className="max-w-sm pt-2 text-md text-gray-600">{description}</p>
        </article>
      </BlurFade>
    </li>
  );
}
